import {
  Container,
  Title,
  Text,
  Stack,
  Grid,
  Card,
  Group,
  Image,
  Box,
} from '@mantine/core';
import markHeadshot from '../../assets/mark-lyda.webp';
import ryanHeadshot from '../../assets/ryan-peterson.webp';
import PublicFooter from '../../components/PublicFooter/PublicFooter';
import './About.css';

const About = () => {
  return (
    <>
      <Container size='lg' py='xl'>
        <Stack gap='xl'>
          {/* Hero Section */}
          <div>
            <Title order={1} mb='md' size='h1'>
              About Practist
            </Title>
            <Text size='lg' c='dimmed' style={{ maxWidth: '800px' }}>
              We're building legal practice management software that makes law
              firms more accessible, efficient, and profitable.
            </Text>
          </div>

          {/* Story Section */}
          <Card shadow='sm' padding='xl' radius='md' withBorder>
            <Stack gap='md'>
              <Title order={2} size='h2'>
                Our Story
              </Title>
              <Text>
                Practist was born out of our law firm's frustrations with
                existing practice management options. After years of dealing
                with clunky software that required constant workarounds, we
                realized that most of our inefficiencies weren't from the
                practice of law itself—they came from the tools we were using.
              </Text>
              <Text>
                We knew there had to be a better way—a platform that brings
                everything together from day one, without the complexity and
                cost of traditional legal tech. So we built our own.
              </Text>
              <Text>
                The result? We reduced our admin time by 70%, giving us more
                time to focus on what matters most—practicing law and serving
                our clients. Now we're bringing that same efficiency to law
                firms everywhere.
              </Text>
            </Stack>
          </Card>

          {/* Team Section */}
          <Card shadow='sm' padding='xl' radius='md' withBorder>
            <Stack gap='md'>
              <Title order={2} size='h2'>
                Our Founders
              </Title>
              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Card
                    shadow='sm'
                    padding='lg'
                    radius='md'
                    withBorder
                    h='100%'
                  >
                    <Stack gap='md'>
                      <Box
                        style={{
                          width: 150,
                          height: 150,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          margin: '0 auto',
                        }}
                      >
                        <Image
                          src={markHeadshot}
                          alt='Mark Lyda'
                          width={150}
                          height={150}
                          style={{ objectFit: 'cover' }}
                        />
                      </Box>
                      <Title order={3} size='h3' ta='center'>
                        Mark Lyda
                      </Title>
                      <Text fw={500} ta='center' c='dimmed'>
                        Cofounder & CEO
                      </Text>
                      <Text ta='center' c='dimmed'>
                        Mark is an expert on law firms. He founded his firm in
                        2014 and grew it to a national firm that is in the top
                        5% of U.S. law firms by number of attorneys. He has
                        represented law firms, mentored firm founders, and
                        worked with regulators to create nation-leading tools
                        for ethical firm management. He is the host of the "How
                        to Start and Run a Law Firm" podcast. His experience
                        spans big and small firms, government, and military
                        service. Mark holds a J.D. from Notre Dame, has been
                        recognized by SuperLawyers nine times, and has received
                        the Air Force Commendation Medal twice.
                      </Text>
                    </Stack>
                  </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Card
                    shadow='sm'
                    padding='lg'
                    radius='md'
                    withBorder
                    h='100%'
                  >
                    <Stack gap='md'>
                      <Box
                        style={{
                          width: 150,
                          height: 150,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          margin: '0 auto',
                        }}
                      >
                        <Image
                          src={ryanHeadshot}
                          alt='Ryan Peterson'
                          width={150}
                          height={150}
                          style={{ objectFit: 'cover' }}
                        />
                      </Box>
                      <Title order={3} size='h3' ta='center'>
                        Ryan Peterson
                      </Title>
                      <Text fw={500} ta='center' c='dimmed'>
                        Cofounder & CTO
                      </Text>
                      <Text ta='center' c='dimmed'>
                        Ryan builds frictionless and reliable software. He has
                        been a professional software developer since 2009. Ryan
                        was employee #3 at Postmates, where he wrote a majority
                        of the iOS code, creating a frictionless experience for
                        consumers and service providers. He was a major
                        contributor to early strategic decisions at the company.
                        Ryan also served as Tech Lead for the Financial Times
                        app, which won Apple's Design Award at WWDC. As a
                        private developer, he was a primary contributor to
                        Stitch Fix's first mobile app. He has held senior
                        positions at HouseCanary, Reclip and Product Science,
                        Inc.
                      </Text>
                    </Stack>
                  </Card>
                </Grid.Col>
              </Grid>
            </Stack>
          </Card>

          {/* Philosophy Section */}
          <Card shadow='sm' padding='xl' radius='md' withBorder>
            <Stack gap='md'>
              <Title order={2} size='h2'>
                Our Philosophy
              </Title>
              <Text>We believe that great legal software should be:</Text>
              <Stack gap='sm' pl='md'>
                <Text>
                  <strong>Simple, not simplistic.</strong> Powerful features
                  don't have to be complicated. We design for clarity and ease
                  of use without sacrificing functionality.
                </Text>
                <Text>
                  <strong>Integrated by default.</strong> Your intake, billing,
                  documents, and AI tools should work together seamlessly from
                  the moment you start using the platform.
                </Text>
                <Text>
                  <strong>Accessible to all.</strong> We're committed to making
                  legal practice management affordable and approachable for
                  firms of all sizes, especially those dedicated to access to
                  justice.
                </Text>
                <Text>
                  <strong>Built for attorneys, by attorneys.</strong> We
                  understand the unique challenges of legal practice because
                  we've lived them. Every feature is designed with real-world
                  workflows in mind.
                </Text>
              </Stack>
            </Stack>
          </Card>
        </Stack>
      </Container>
      <PublicFooter />
    </>
  );
};

export default About;
